
import { defineComponent } from '@nuxtjs/composition-api';
import JobsPlaceholderCurrentJobsInquiryQuery from './JobsPlaceholderCurrentJobsInquiry.query';
import BillIcon from './images/bill.vue.svg';
import CheckIcon from './images/check.vue.svg';
import PieIcon from './images/pie.vue.svg';
import VideoIcon from './images/video.vue.svg';
import BravadoButtonV3 from '@/components/common/BravadoButtonV3/BravadoButtonV3.vue';
import { JobsInquiryState } from '@/types/graphql';
import type { JobsInquiry } from '@/types/graphql';
import { getOnboardingV2Steps } from '@/components/partials/jobs/onboarding/utils';
import type { JobsOnboardingStep } from '@/components/partials/jobs/onboarding/types';
import { getPortfolioPath } from '@/utils/routes';

export default defineComponent({
  components: { BravadoButtonV3, BillIcon, CheckIcon, PieIcon, VideoIcon },
  apollo: {
    currentJobsInquiry: {
      query: JobsPlaceholderCurrentJobsInquiryQuery,
      skip(): boolean {
        return !this.$auth.loggedIn;
      },
      error(e): void {
        this.$notice.error(e);
      },
    },
  },
  data(): { redirected: boolean; currentJobsInquiry?: JobsInquiry } {
    return {
      redirected: false,
      currentJobsInquiry: undefined,
    };
  },
  computed: {
    benefits() {
      return [
        {
          id: 'bonus',
          icon: 'BillIcon',
          label: `<strong>$100 success fee</strong> if you get hired through us`,
        },
        {
          id: 'check',
          icon: 'CheckIcon',
          label: '<strong>Vetted, fast-growing</strong> companies where reps are hitting quota',
        },
        {
          id: 'pie',
          icon: 'PieIcon',
          label: '<strong>Realistic quotas</strong> and <strong>OTEs</strong> you can hit and exceed',
        },
        { id: 'video', icon: 'VideoIcon', label: 'Interview <strong>coaching</strong> from top sales leaders' },
      ];
    },
    steps(): JobsOnboardingStep[] | undefined {
      return getOnboardingV2Steps();
    },
    step(): JobsOnboardingStep | undefined {
      return this.steps?.find(step => step.key === this.currentJobsInquiry?.onboardingStateV2);
    },
    progress(): number {
      if (!this.steps) return 0;

      if (!this.step) {
        return 0;
      }

      const index = this.steps.findIndex(step => step.key === this.currentJobsInquiry?.onboardingStateV2);
      return Math.round((index / this.steps.length) * 100);
    },
    description(): string {
      if (this.isFilledOnboarding) {
        return 'You successfully filled out your application!';
      }

      if (this.progress > 0) {
        return `Your application is ${this.progress}% done! Continue anytime.`;
      }

      return 'Salespeople get screwed all the time on empty promises. We are changing how sales hiring is done.';
    },
    buttonTitle(): string {
      if (this.isFilledOnboarding) {
        return 'Open Profile';
      }

      if (this.progress > 0) {
        return 'Continue the survey';
      }

      return 'Apply Now';
    },
    isVisible(): boolean {
      return !this.$auth.loggedIn || this.currentJobsInquiry !== undefined;
    },
    isFilledOnboarding(): boolean {
      if (!this.currentJobsInquiry) return false;

      return this.currentJobsInquiry.state !== JobsInquiryState.Initialized;
    },
  },
  methods: {
    async handleApply(): Promise<void> {
      if (this.redirected) {
        return;
      }

      if (!this.$auth.loggedIn) {
        return this.$modal.showWithRoute(
          { custom: this.$constants.dynamicModals.WAR_ROOM_SIGN_UP_MODAL },
          { refferer: '/jobs' },
        );
      }

      this.redirected = true;

      if (this.isFilledOnboarding) {
        await this.$router.push(getPortfolioPath(this.$auth.user.slug!));
        return;
      }

      this.$analytics.logEvent('jobs__welcome__survey__start', { continue: !!this.currentJobsInquiry });

      window.location.href = `/b2c/?toLastStep=true`;
    },
  },
});
